import { useId, useLayoutEffect, useRef } from 'react'
import { motion } from '@/utils/motion'
import type { SrsLadderRow } from '@/types/admin'
import { curvePath, lerp, type Point } from './lab/labGeometry'
import { RATING_COLOR } from './ratingColors'

/**
 * How a card's interval grows, drawn as one curve per rating.
 *
 * The box ladder this replaces was honest but flat: a row of numbers per rating, with
 * the shape of the curve left for the reader to imagine. Here the shape *is* the
 * picture. Review number runs left to right, the interval climbs on a log scale (a
 * linear one would flatten the first month into a hairline under a 180-day bar), and
 * the mastery threshold is a horizon the curves are trying to reach. A curve that
 * crosses it earns a glowing point where the card leaves the queue; a curve that
 * never gets there tails off short.
 *
 * Every slider change asks the server for a new projection and the curves morph to
 * it rather than snap, so the admin sees *how* a parameter bends the schedule, not
 * just where it ends up. Geometry is animated on the DOM directly via `motion.tween`
 * — re-rendering React sixty times a second for a diagram would be the wrong tool.
 */

const W = 640
const H = 340
const PAD = { top: 22, right: 76, bottom: 40, left: 46 }
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom
/** Mirrors Srs::Projection::MAX_STEPS, so every row has a slot per possible review. */
const MAX_STEPS = 12

const Y_TICKS: { days: number; label: string }[] = [
  { days: 1, label: '1d' },
  { days: 7, label: '1w' },
  { days: 30, label: '1mo' },
  { days: 90, label: '3mo' },
  { days: 365, label: '1y' },
]

interface RowFrame {
  points: Point[]
  /** 1 for a review that exists in this projection, 0 for a padded slot. */
  alive: number[]
  /** Index of the last real review; the halo and label hang off it. */
  last: number
  mastered: number
}

interface Frame {
  rows: RowFrame[]
  masteryY: number
  knowWellY: number
}

function ceilingFor(ladder: SrsLadderRow[], mastery: number) {
  const longest = Math.max(0, ...ladder.flatMap((row) => row.intervals.map((s) => s.days)))
  return Math.max(mastery * 1.6, longest * 1.15, 30)
}

function scales(ladder: SrsLadderRow[], mastery: number) {
  const ceiling = ceilingFor(ladder, mastery)
  const steps = Math.max(6, ...ladder.map((row) => row.intervals.length))
  const x = (i: number) => PAD.left + (i / (steps - 1)) * PLOT_W
  const y = (days: number) =>
    PAD.top + (1 - Math.log(days + 1) / Math.log(ceiling + 1)) * PLOT_H
  return { x, y, ceiling, steps }
}

function frameFor(ladder: SrsLadderRow[], mastery: number, knowWell: number): Frame {
  const { x, y } = scales(ladder, mastery)
  const rows = ladder.map((row) => {
    const real = row.intervals.map((step, i) => ({ x: x(i), y: y(step.days) }))
    const last = Math.max(0, real.length - 1)
    const tail = real[last] ?? { x: x(0), y: y(0) }
    const points = Array.from({ length: MAX_STEPS }, (_, i) => real[i] ?? tail)
    const alive = Array.from({ length: MAX_STEPS }, (_, i) => (i < real.length ? 1 : 0))
    const mastered = row.intervals[last]?.status === 'mastered' ? 1 : 0
    return { points, alive, last, mastered }
  })
  return { rows, masteryY: y(mastery), knowWellY: y(knowWell) }
}

function blend(from: Frame, to: Frame, t: number): Frame {
  return {
    masteryY: lerp(from.masteryY, to.masteryY, t),
    knowWellY: lerp(from.knowWellY, to.knowWellY, t),
    rows: to.rows.map((row, r) => {
      const prev = from.rows[r] ?? row
      return {
        last: row.last,
        mastered: lerp(prev.mastered, row.mastered, t),
        alive: row.alive.map((a, i) => lerp(prev.alive[i] ?? a, a, t)),
        points: row.points.map((p, i) => ({
          x: lerp(prev.points[i]?.x ?? p.x, p.x, t),
          y: lerp(prev.points[i]?.y ?? p.y, p.y, t),
        })),
      }
    }),
  }
}

function elapsedDays(row: SrsLadderRow) {
  return Math.round(row.intervals.slice(0, -1).reduce((sum, step) => sum + step.days, 0))
}

function summary(row: SrsLadderRow) {
  const last = row.intervals[row.intervals.length - 1]
  if (last?.status !== 'mastered') return 'never reaches mastery on its own'
  return `mastered after ${row.intervals.length} reviews, about ${elapsedDays(row)} days`
}

interface IntervalCurvesProps {
  ladder: SrsLadderRow[]
  masteryThreshold: number
  knowWellInterval: number
  /** Accessible name; two charts share the admin page, so each names its own story. */
  label?: string
}

export function IntervalCurves({
  ladder,
  masteryThreshold,
  knowWellInterval,
  label = 'Interval growth per rating',
}: IntervalCurvesProps) {
  // SVG ids are document-global; a second chart on the page must not reuse them.
  const uid = `curves${useId().replace(/[^a-zA-Z0-9]/g, '')}`
  const svgRef = useRef<SVGSVGElement>(null)
  const shown = useRef<Frame | null>(null)
  const { ceiling, steps, x, y } = scales(ladder, masteryThreshold)

  useLayoutEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    const target = frameFor(ladder, masteryThreshold, knowWellInterval)
    const paint = (frame: Frame) => {
      svg.querySelector<SVGLineElement>('[data-mastery]')?.setAttribute('y1', `${frame.masteryY}`)
      svg.querySelector<SVGLineElement>('[data-mastery]')?.setAttribute('y2', `${frame.masteryY}`)
      svg.querySelector<SVGRectElement>('[data-mastery-band]')?.setAttribute('height', `${Math.max(0, frame.masteryY - PAD.top)}`)
      svg.querySelector<SVGTextElement>('[data-mastery-label]')?.setAttribute('y', `${frame.masteryY - 6}`)
      svg.querySelector<SVGLineElement>('[data-know-well]')?.setAttribute('y1', `${frame.knowWellY}`)
      svg.querySelector<SVGLineElement>('[data-know-well]')?.setAttribute('y2', `${frame.knowWellY}`)
      svg.querySelector<SVGTextElement>('[data-know-well-label]')?.setAttribute('y', `${frame.knowWellY - 6}`)
      frame.rows.forEach((row, r) => {
        const g = svg.querySelector<SVGGElement>(`[data-row="${r}"]`)
        if (!g) return
        g.querySelector('path')?.setAttribute('d', curvePath(row.points, PAD.top + PLOT_H))
        g.querySelectorAll<SVGCircleElement>('[data-dot]').forEach((dot, i) => {
          dot.setAttribute('cx', `${row.points[i].x}`)
          dot.setAttribute('cy', `${row.points[i].y}`)
          dot.setAttribute('opacity', `${row.alive[i]}`)
        })
        const end = row.points[row.last]
        const halo = g.querySelector<SVGCircleElement>('[data-halo]')
        halo?.setAttribute('cx', `${end.x}`)
        halo?.setAttribute('cy', `${end.y}`)
        halo?.setAttribute('opacity', `${row.mastered}`)
        const label = g.querySelector<SVGTextElement>('[data-label]')
        label?.setAttribute('x', `${end.x + 12}`)
        label?.setAttribute('y', `${Math.min(end.y + 4, PAD.top + PLOT_H - 8)}`)
      })
    }

    const from = shown.current
    shown.current = target
    if (!from) {
      paint(target)
      void motion.drawIn(svg.querySelectorAll<SVGPathElement>('[data-row] path'))
      return
    }
    void motion.tween((t) => paint(blend(from, target, t)), { key: svg })
  }, [ladder, masteryThreshold, knowWellInterval])

  const ticks = Y_TICKS.filter((tick) => tick.days <= ceiling)

  return (
    <figure className="space-y-5">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full overflow-visible font-mono"
        role="img"
        aria-label={label}
        aria-describedby={`${uid}-desc`}
      >
        <title>{label}</title>
        <desc id={`${uid}-desc`}>
          {ladder.map((row) => `${row.label}: ${summary(row)}.`).join(' ')}
        </desc>

        <defs>
          <linearGradient id={`${uid}-band`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--color-accent)" stopOpacity={0} />
            <stop offset="1" stopColor="var(--color-accent)" stopOpacity={0.18} />
          </linearGradient>
          <filter id={`${uid}-glow`} x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation={3} />
          </filter>
        </defs>

        {/* Above the mastery bar: where a card is out of the queue. */}
        <rect
          data-mastery-band
          x={PAD.left}
          y={PAD.top}
          width={PLOT_W}
          height={0}
          fill={`url(#${uid}-band)`}
        />

        {ticks.map((tick) => (
          <g key={tick.days}>
            <line
              x1={PAD.left}
              x2={PAD.left + PLOT_W}
              y1={y(tick.days)}
              y2={y(tick.days)}
              stroke="var(--color-line-soft)"
            />
            <text
              x={PAD.left - 10}
              y={y(tick.days) + 3.5}
              textAnchor="end"
              fontSize={10}
              fill="var(--color-muted)"
            >
              {tick.label}
            </text>
          </g>
        ))}

        {Array.from({ length: steps }, (_, i) => (
          <text
            key={i}
            x={x(i)}
            y={H - PAD.bottom + 18}
            textAnchor="middle"
            fontSize={10}
            fill="var(--color-muted)"
          >
            {i + 1}
          </text>
        ))}
        <text
          x={PAD.left + PLOT_W / 2}
          y={H - 6}
          textAnchor="middle"
          fontSize={9}
          letterSpacing={2}
          fill="var(--color-muted)"
        >
          REVIEW
        </text>

        <line
          data-know-well
          x1={PAD.left}
          x2={PAD.left + PLOT_W}
          stroke="var(--color-muted)"
          strokeDasharray="2 5"
          opacity={0.7}
        />
        <text
          data-know-well-label
          x={PAD.left + 4}
          textAnchor="start"
          fontSize={9}
          letterSpacing={1.5}
          fill="var(--color-muted)"
        >
          KNOW WELL · {knowWellInterval}D
        </text>

        <line
          data-mastery
          x1={PAD.left}
          x2={PAD.left + PLOT_W}
          stroke="var(--color-accent)"
          strokeDasharray="6 5"
          strokeWidth={1.25}
        />
        <text
          data-mastery-label
          x={PAD.left + 4}
          textAnchor="start"
          fontSize={9}
          letterSpacing={1.5}
          fill="var(--color-accent)"
        >
          MASTERY · {masteryThreshold}D
        </text>

        {ladder.map((row, r) => (
          <g key={row.rating} data-row={r} style={{ color: RATING_COLOR[row.rating] }}>
            <path fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
            {Array.from({ length: MAX_STEPS }, (_, i) => (
              <circle key={i} data-dot r={3} fill="var(--color-card)" stroke="currentColor" strokeWidth={1.5} />
            ))}
            <circle
              data-halo
              r={9}
              fill="currentColor"
              opacity={0}
              filter={`url(#${uid}-glow)`}
              className="motion-safe:animate-pulse"
              style={{ fillOpacity: 0.6 }}
            />
            <text data-label fontSize={10} letterSpacing={1} fill="currentColor">
              {row.label.toUpperCase()}
            </text>
          </g>
        ))}
      </svg>

      <figcaption>
        <dl className="grid gap-2 sm:grid-cols-2">
          {ladder.map((row) => (
            <div key={row.rating} className="flex items-baseline gap-3">
              <dt className="flex shrink-0 items-center gap-2 font-mono text-xs uppercase tracking-widest text-body">
                <span
                  aria-hidden
                  className="inline-block h-2 w-5 rounded-full"
                  style={{ background: RATING_COLOR[row.rating] }}
                />
                {row.label}
              </dt>
              <dd className="font-serif text-sm text-muted">{summary(row)}</dd>
            </div>
          ))}
        </dl>
      </figcaption>
    </figure>
  )
}
